/**
 * Historical Significance Section Component
 * Displays historical significance information with bullet points
 * Follows Single Responsibility Principle - only responsible for rendering the section
 */

import React, { useMemo } from 'react';
import Image from 'next/image';
import { spacing, borders } from '@/design-system/utils';
import { SectionHeader } from '@/components/ui/section-header';
import { HistoricalSignificanceList } from './HistoricalSignificanceList';
import {
  HistoricalSignificanceSectionProps,
  HistoricalSignificanceItemData,
} from './types';
import { parseHistoricalSignificanceItems } from './utils';

/**
 * Historical Significance Section Component
 * Renders a section with title, optional image, and list of historical significance items
 * Each item has a heading and description with bullet point
 * Supports both string[] (legacy) and HistoricalSignificanceItem[] (structured) formats
 */
export const HistoricalSignificanceSection: React.FC<
  HistoricalSignificanceSectionProps
> = ({ title, items, image, className }) => {
  // Parse items - handle both string[] and HistoricalSignificanceItemData[] formats
  const parsedItems = useMemo<HistoricalSignificanceItemData[]>(() => {
    if (!items || items.length === 0) {
      return [];
    }

    // Check if items are already structured (HistoricalSignificanceItemData[])
    if (typeof items[0] === 'object' && 'heading' in items[0]) {
      return items as HistoricalSignificanceItemData[];
    }

    // Parse string items into structured format
    return parseHistoricalSignificanceItems(items as string[]);
  }, [items]);

  if (parsedItems.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[3],
      }}
      className={className}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: spacing[4], // 16px gap
          padding: `${spacing[3]} ${spacing[3]} ${spacing[6]} ${spacing[3]}`, // 12px 12px 24px 12px (top, right, bottom, left)
          borderRadius: borders.radius.xl, // 12px
          border: '1px solid #F5F5F4',
          background: '#FFFFFF',
          alignSelf: 'stretch',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing[2],
            width: '100%',
          }}
        >
          <div style={{ flex: 1 }}>
            <SectionHeader title={title} hasBackground={false} />
          </div>
          {image && (
            <div
              style={{
                position: 'relative',
                width: 140,
                height: 56,
                borderRadius: borders.radius.md,
                overflow: 'hidden',
                flexShrink: 0,
              }}
            >
              <Image
                src={image}
                alt={title}
                fill
                style={{ objectFit: 'cover' }}
              />
            </div>
          )}
        </div>
        <HistoricalSignificanceList items={parsedItems} />
      </div>
    </div>
  );
};
